<template>
  <v-card class="bg-pwt_snow" elevation="0" flat>
    <!-- fleet info card section -->
    <v-card-text>
      <FleetLogoCard class="mb-5" />
    </v-card-text>
    <!-- members section -->
    <v-card flat class="bg-pwt_snow">
      <v-card-title> Members </v-card-title>
      <!-- members section: search bar -->
      <v-card-text>
        <v-text-field
          v-model="searchQuery"
          label="Search fleet members"
          outlined
          clearable
          @input="filterMembers"
          bg-color="transparent"
        >
          <template v-if="can_invite" v-slot:append-inner>
            <CustomSolidButton narrow @click.stop="() => (show_invite_dialog = true)" >
              + Invite members
            </CustomSolidButton>
          </template>
        </v-text-field>
      </v-card-text>
      <!-- members section: list row -->
      <v-card-text class="d-flex ga-2">
        <MemberCard v-if="currentUser" :user="currentUser" />
        <MemberCard
          v-for="(user, index) in filteredUsers"
          :key="'member-card-' + index"
          :user="user"
        />
      </v-card-text>
    </v-card>
    <!-- saved trips section -->

    <!-- vehicles section -->
    <v-card flat class="bg-pwt_snow">
      <v-card-title>
        <v-row no-gutters align="center">
          <span> Fleet vehicles </span>
          <v-spacer />
          <v-btn v-if="smAndDown" icon @click="() => emit('view-vehicles')">
            <v-icon color="grey-lighten-1">mdi-dots-vertical</v-icon>
          </v-btn>
          <TextBtn v-else @click="() => emit('view-vehicles')"> View all </TextBtn>
        </v-row>
      </v-card-title>
      <v-card-text>
        <span v-if="!vehicleList.length" class="py-5">
          You don't have any vehicles in your fleet
        </span>
        <v-row no-gutters v-else>
          <v-card
            flat
            v-for="(vehicle, index) in vehicleList"
            :key="'vehicle-' + index"
            :style="smAndDown ? 'width: 100%' : 'width: 50%'"
            class="pa-md-1 py-1"
          >
            <VehicleListCard
              :vehicle="vehicle"
              @click="() => emit('view-vehicle', vehicle.localId)"
              :selected="false"
            />
          </v-card>
        </v-row>
        <v-row no-gutters v-if="vehicleList.length">
          <v-spacer />
          <ElevatedBtn @click="showBookingDialog = true" class="mt-3">
            Book fleet vehicles
          </ElevatedBtn>
        </v-row>
        <GenericDialog :activator="showBookingDialog" @close="showBookingDialog = false" small>
          <v-card-title> Book fleet vehicles </v-card-title>
          <v-card-subtitle>Premium feature</v-card-subtitle>
          <v-card-text>
            Please contact PowerTrip to discuss unlocking premium features
          </v-card-text>
        </GenericDialog>
      </v-card-text>
    </v-card>
    <!-- locations section -->
    <v-card flat class="bg-pwt_snow">
      <v-card-title>
        <v-row no-gutters align="center">
          <span> Fleet locations </span>
          <v-spacer />
          <TextBtn v-if="mdAndUp" @click="() => emit('view-locations')"> View all </TextBtn>
        </v-row>
      </v-card-title>
      <v-card-text>
        <span v-if="!fleetLocationsList.length" class="py-5">
          You don't have any saved locations
        </span>

        <v-row no-gutters>
          <v-col
            cols="12"
            md="6"
            v-for="(location, index) in fleetLocationsList"
            :key="'location-card-' + index"
            :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
            class="mb-3"
          >
            <LocationDisplayCard
              :location="location"
              @click="() => navigateToLocationDetails(location)"
            />
          </v-col>
        </v-row>
        <ElevatedBlockBtn v-if="smAndDown" class="mt-3" @click="() => emit('view-locations')">
          See all fleet locations
        </ElevatedBlockBtn>
      </v-card-text>
    </v-card>
    <!-- invite dialog -->
    <GenericDialog :activator="show_invite_dialog" @close="close_invite_dialog" small>
      <v-card-title> Invite User </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="invite_email"
          label="Email"
          outlined
          clearable
          :error-messages="email_validation_error"
          @change="clear_invite_errors"
        />
        <v-alert v-if="invite_error" type="error" class="mt-2">
          {{ invite_error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <OutlinedBtn @click="close_invite_dialog"> Cancel </OutlinedBtn>
        <ElevatedBtn
          color="primary"
          @click="invite_user"
          :disabled="!invite_email.trim() || !!email_validation_error || !!invite_error"
        >
          Invite
        </ElevatedBtn>
      </v-card-actions>
    </GenericDialog>
  </v-card>
</template>

<script lang="ts" setup>
import { createInvite } from '@/api/calls/backend-calls/invite-calls'
import FavouriteLocation from '@/classes/favouriteLocation'
import { PermissionKeys } from '@/classes/user_classes/essentialsPermissionsControl'
import Vehicle from '@/classes/vehicle_classes/vehicle'
import LocationDisplayCard from '@/components/location-manager/dashboard-components/LocationDisplayCard.vue'
import CustomSolidButton from '@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue'
import OutlinedBtn from '@/components/ui-elements/buttons/OutlinedBtn.vue'
import { GettersTypes, MutationTypes, type State } from '@/store/store_types'
import { useDashboardNavStore } from '@/store/stores/dashboard_nav.store'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import GenericDialog from '../../dialog-wrappers/GenericDialog.vue'
import ElevatedBlockBtn from '../../ui-elements/buttons/ElevatedBlockBtn.vue'
import ElevatedBtn from '../../ui-elements/buttons/ElevatedBtn.vue'
import TextBtn from '../../ui-elements/buttons/TextBtn.vue'
import VehicleListCard from '../../vehicles/VehicleListCard.vue'
import FleetLogoCard from './FleetLogoCard.vue'
import MemberCard from './MemberCard.vue'

const emit = defineEmits<{
  (e: 'view-vehicle', id: string | undefined): void
  (e: 'view-vehicles'): void
  (e: 'view-locations'): void
}>()

const store = useStore<State>()
const { smAndDown, mdAndUp } = useDisplay()

const showBookingDialog = ref(false)

const currentUser = computed(() => store.getters[GettersTypes.getCurrentUser])
const otherUsers = computed(() => {
  const allUsers = store.state.users
  return allUsers.filter((user) => user.id !== currentUser.value?.id)
})

const vehicleList = computed<Vehicle[]>(() => {
  const returnList: Vehicle[] = []
  const fullList = store.getters[GettersTypes.connectedVehicles] as Vehicle[]

  fullList.forEach((vehicle, index) => {
    if (index < (smAndDown ? 3 : 4)) returnList.push(vehicle)
  })

  return returnList
})
const fleetLocationsList = computed<FavouriteLocation[]>(() => {
  const unfilteredList = store.state.favLocations
  return unfilteredList.filter((location) => location.visibility !== 'private').slice(0, 4)
})

const dashboardNavStore = useDashboardNavStore()

const navigateToLocationDetails = (location: FavouriteLocation) => {
  store.commit(MutationTypes.setSelectedFavLocation, location)
  dashboardNavStore.setDashboardDialogContent('fleet-location-details')
}

// ---------------------------------------------------------------------------- //
// ------------------------------- SEARCH LOGIC ------------------------------- //
// ---------------------------------------------------------------------------- //

const searchQuery = ref('')
const filterMembers = () => {}
const filteredUsers = computed(() => {
  if (!searchQuery.value.trim()) return otherUsers.value
  const query = searchQuery.value.toLowerCase()
  return otherUsers.value.filter((user) => {
    const displayName = user.displayName?.toLowerCase() || ''
    const email = user.email?.toLowerCase() || ''
    return displayName.includes(query) || email.includes(query)
  })
})

// ---------------------------------------------------------------------------- //
// ------------------------------- INVITE LOGIC ------------------------------- //
// ---------------------------------------------------------------------------- //

const can_invite = computed(() => {
  // only fleet managers can invite users
  return store.state.permissions?.get(PermissionKeys.canInviteUsers)
})

const show_invite_dialog = ref(false)

const invite_email = ref('')
const email_validation_error = ref<string | null>(null)
const invite_error = ref<string | null>(null)

const invite_user = async () => {
  clear_invite_errors()
  if (!can_invite.value) {
    invite_error.value = 'You do not have permission to invite users to this fleet.'
    return
  }
  // validation for email format could be added here before making the API call
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(invite_email.value.trim())) {
    email_validation_error.value = 'Please enter a valid email address.'
    return
  }
  try {
    await createInvite(invite_email.value)
    // if successful, close dialog and reset state
    close_invite_dialog()
  } catch (error) {
    invite_error.value = 'Failed to send invite. Please check the email and try again.'
  }
}

const clear_invite_errors = () => {
  invite_error.value = null
  email_validation_error.value = null
}

const close_invite_dialog = () => {
  show_invite_dialog.value = false
  invite_email.value = ''
  clear_invite_errors()
}
</script>
